import { db } from '../firebase'
import {
  collection,
  addDoc,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore'

// TypeScript interfaces
export interface PracticeLog {
  id?: string
  studentId: string
  studentName?: string
  piece: string
  duration: number // in minutes
  notes?: string
  date: string // ISO date string
  createdAt?: Timestamp
  updatedAt?: Timestamp
}

export interface PracticeLogData {
  studentId: string
  studentName?: string
  piece: string
  duration: number
  notes?: string
  date: string
}

/**
 * Create a new practice log entry
 */
export const createPracticeLog = async (
  teacherId: string,
  logData: PracticeLogData,
): Promise<string> => {
  try {
    console.log('Creating practice log for teacher:', teacherId, logData)

    if (
      !teacherId ||
      !logData.studentId ||
      !logData.piece ||
      !logData.duration
    ) {
      throw new Error(
        'Missing required fields: teacherId, studentId, piece, or duration',
      )
    }

    const logsRef = collection(db, 'teachers', teacherId, 'practiceLogs')

    const dataToSave = {
      ...logData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }

    const newLogRef = await addDoc(logsRef, dataToSave)
    console.log('Practice log created with ID:', newLogRef.id)

    return newLogRef.id
  } catch (error) {
    console.error('Error creating practice log:', error)
    throw error
  }
}

/**
 * Get all practice logs for a teacher
 */
export const getTeacherPracticeLogs = async (
  teacherId: string,
  startDate?: string,
  endDate?: string,
): Promise<PracticeLog[]> => {
  try {
    console.log('Fetching practice logs for teacher:', teacherId)

    if (!teacherId) {
      throw new Error('Teacher ID is required')
    }

    const logsRef = collection(db, 'teachers', teacherId, 'practiceLogs')
    let logsQuery = query(logsRef, orderBy('date', 'desc'))

    // Add date range filters if provided
    if (startDate && endDate) {
      logsQuery = query(
        logsRef,
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'desc'),
      )
    }

    const logsSnapshot = await getDocs(logsQuery)

    const logs: PracticeLog[] = logsSnapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as PracticeLog,
    )

    console.log(`Found ${logs.length} practice logs`)
    return logs
  } catch (error) {
    console.error('Error fetching practice logs:', error)
    throw error
  }
}

/**
 * Get practice logs for a specific student
 */
export const getStudentPracticeLogs = async (
  teacherId: string,
  studentId: string,
  startDate?: string,
  endDate?: string,
): Promise<PracticeLog[]> => {
  try {
    console.log('Fetching practice logs for student:', studentId)

    if (!teacherId || !studentId) {
      throw new Error('Teacher ID and Student ID are required')
    }

    const logsRef = collection(db, 'teachers', teacherId, 'practiceLogs')
    let logsQuery = query(
      logsRef,
      where('studentId', '==', studentId),
      orderBy('date', 'desc'),
    )

    // Add date range filters if provided
    if (startDate && endDate) {
      logsQuery = query(
        logsRef,
        where('studentId', '==', studentId),
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'desc'),
      )
    }

    const logsSnapshot = await getDocs(logsQuery)

    const logs: PracticeLog[] = logsSnapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as PracticeLog,
    )

    console.log(`Found ${logs.length} practice logs for student`)
    return logs
  } catch (error) {
    console.error('Error fetching student practice logs:', error)
    throw error
  }
}

/**
 * Update a practice log entry
 */
export const updatePracticeLog = async (
  teacherId: string,
  logId: string,
  updates: Partial<PracticeLogData>,
): Promise<boolean> => {
  try {
    console.log('Updating practice log:', logId, updates)

    if (!teacherId || !logId) {
      throw new Error('Teacher ID and Log ID are required')
    }

    const logRef = doc(db, 'teachers', teacherId, 'practiceLogs', logId)

    await updateDoc(logRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    })

    console.log('Practice log updated successfully')
    return true
  } catch (error) {
    console.error('Error updating practice log:', error)
    throw error
  }
}

/**
 * Delete a practice log entry
 */
export const deletePracticeLog = async (
  teacherId: string,
  logId: string,
): Promise<boolean> => {
  try {
    console.log('Deleting practice log:', logId)

    if (!teacherId || !logId) {
      throw new Error('Teacher ID and Log ID are required')
    }

    const logRef = doc(db, 'teachers', teacherId, 'practiceLogs', logId)
    await deleteDoc(logRef)

    console.log('Practice log deleted successfully')
    return true
  } catch (error) {
    console.error('Error deleting practice log:', error)
    throw error
  }
}

/**
 * Calculate total practice hours for a student in a date range
 */
export const calculateStudentPracticeHours = async (
  teacherId: string,
  studentId: string,
  startDate: string,
  endDate: string,
): Promise<number> => {
  try {
    const logs = await getStudentPracticeLogs(
      teacherId,
      studentId,
      startDate,
      endDate,
    )

    const totalMinutes = logs.reduce((sum, log) => sum + log.duration, 0)
    const totalHours = totalMinutes / 60

    console.log(`Total practice hours for student ${studentId}: ${totalHours}`)
    return totalHours
  } catch (error) {
    console.error('Error calculating practice hours:', error)
    throw error
  }
}

/**
 * Get practice hours trend data for charts
 */
export const getPracticeHoursTrend = async (
  teacherId: string,
  startDate: string,
  endDate: string,
  groupBy: 'day' | 'week' | 'month' = 'week',
): Promise<Array<{ period: string; hours: number }>> => {
  try {
    console.log('Calculating practice hours trend:', {
      startDate,
      endDate,
      groupBy,
    })

    const logs = await getTeacherPracticeLogs(teacherId, startDate, endDate)

    // Group logs by period
    const groupedData: Record<string, number> = {}

    logs.forEach((log) => {
      const date = new Date(log.date)
      let periodKey: string

      switch (groupBy) {
        case 'day':
          periodKey = log.date
          break
        case 'week':
          // Get week number
          const weekStart = new Date(date)
          weekStart.setDate(date.getDate() - date.getDay())
          periodKey = weekStart.toISOString().split('T')[0]
          break
        case 'month':
          periodKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
          break
        default:
          periodKey = log.date
      }

      if (!groupedData[periodKey]) {
        groupedData[periodKey] = 0
      }
      groupedData[periodKey] += log.duration / 60 // Convert to hours
    })

    // Convert to array and sort
    const trendData = Object.entries(groupedData)
      .map(([period, hours]) => ({ period, hours }))
      .sort((a, b) => a.period.localeCompare(b.period))

    console.log(`Generated ${trendData.length} data points for trend`)
    return trendData
  } catch (error) {
    console.error('Error calculating practice hours trend:', error)
    throw error
  }
}
